import { useEffect, useRef } from "react";

/**
 * utils/modalStack.js
 * Module-level stack of open-modal tokens so Esc closes only the top-most modal when
 * modals are stacked (e.g. a picker opened from inside a form). A modal's Esc listener
 * fires onClose only when its own token is currently the LAST entry in the stack.
 *
 * Mount/unmount only — a modal pushes its token once (on mount) and removes it once
 * (on unmount), by identity (splice), not by position: unmount order isn't guaranteed
 * LIFO (out-of-order unmounts must still leave the remaining stack correct).
 */
export const _stack = [];

export function useModalEsc(onClose) {
  const tokenRef = useRef();
  if (!tokenRef.current) tokenRef.current = {};
  // Keep the latest onClose without re-running the mount/unmount effect on every
  // identity change (most callers pass a fresh inline arrow function each render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const token = tokenRef.current;
    _stack.push(token);

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (_stack[_stack.length - 1] !== token) return;
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const idx = _stack.indexOf(token);
      if (idx !== -1) _stack.splice(idx, 1);
    };
  }, []);
}

/**
 * Backdrop click-to-close that ignores the backdrop's own scroll bar (bug fix, 2026-10-11).
 * `.modal-overlay` is the element that scrolls a tall modal (`overflow-y: auto`), so its scroll
 * bar is part of the overlay itself — a plain `onMouseDown={onClose}` closed the window the
 * moment someone grabbed the scroll bar to drag it. A press whose x/y falls past the overlay's
 * `clientWidth`/`clientHeight` (the area the scroll bars occupy) is ignored; anywhere else on
 * the dark backdrop still closes. jsdom has no layout (`clientWidth` is 0), so the check only
 * applies when a real width exists — tests that click the backdrop keep closing it.
 * Usage: `<div className="modal-overlay" onMouseDown={backdropClose(onClose)}>`.
 */
export function backdropClose(onClose) {
  return (e) => {
    const el = e.currentTarget;
    if (el && el.clientWidth > 0) {
      const r = el.getBoundingClientRect();
      if (e.clientX - r.left >= el.clientWidth || e.clientY - r.top >= el.clientHeight) return;
    }
    onClose(e);
  };
}
